import { Box, Typography } from '@mui/material'
import { GenericDialog, MButton, ProgressLoaders } from 'components'
import { color } from 'config/color'
import { useAuth } from 'context'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { isAuthorized } from 'utils'
import { LinkedAccountCard } from './LinkedAccountCard'

interface IReviewLinkAccountDialog {
  accountsToLink: any
  open: boolean
  onClose: () => void
  onCloseLinkDialog: () => void
  refreshLinkedAccounts: () => Promise<void>
}

export const ReviewLinkAccountDialog = ({
  accountsToLink,
  open,
  onClose,
  onCloseLinkDialog,
  refreshLinkedAccounts
}: IReviewLinkAccountDialog): JSX.Element => {
  const { enqueueSnackbar } = useSnackbar()
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email') ?? ''
  const { linkICAccounts, handleLogOut, getCCSAccount, account } = useAuth()
  const [accountToBeLinked, setAccountToBeLinked] = useState<any>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const initFormValues = async (): Promise<void> => {
    setIsLoading(true)
    try {
      const result: any = await getCCSAccount({ accountList: accountsToLink })
      setAccountToBeLinked(result?.data[0] ?? '')
    } catch (error: any) {
      setAccountToBeLinked(null)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    initFormValues()
  }, [])

  const handleLinkAccounts = async (): Promise<void> => {
    setIsLoading(true)
    try {
      await linkICAccounts({
        accountList: [accountToBeLinked?.accountId],
        email,
        accountId: account?.accountId || '',
        isAdmin: true
      })
      await refreshLinkedAccounts()
      onCloseLinkDialog()
      enqueueSnackbar(`Account ${accountToBeLinked?.accountId} has been linked successfully`, { variant: 'success' })
    } catch (e: any) {
      if (!isAuthorized(e)) await handleLogOut()

      console.log('Error linking account:', e)
      enqueueSnackbar(e?.response?.data?.error || e.message, { variant: 'error' })
      onClose()
    }
    setIsLoading(false)
  }

  return (
    <Box>
      <GenericDialog open={open} onClose={onClose}>
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" flexDirection="column">
            <Typography color={color.grey[900]} fontWeight={500} fontSize={24}>
              Review the information
            </Typography>

            <Typography color={color.grey[700]} fontWeight={400} fontSize={16}>
              Please review the information of each account you are going to
              link.
            </Typography>
          </Box>

          <Box display="flex" flexDirection="column" gap={1}>
            {isLoading
              ? (
                <Box py={5}>
                  <ProgressLoaders height="100%" />{' '}
                </Box>
              )
              : (
                  <Box key={account}>
                    <LinkedAccountCard
                      accountId={accountToBeLinked?.accountId}
                      serviceAddress={accountToBeLinked?.serviceAddress}
                    />
                  </Box>
              )}
          </Box>

          <Box display="flex" flexDirection="column" gap={1}>
            <MButton
              type="button"
              variant="contained"
              rounded="true"
              texttransform="none"
              onClick={handleLinkAccounts}
              disabled={isLoading}
            >
              Submit
            </MButton>

            <MButton
              variant="outlined"
              type="button"
              rounded="true"
              texttransform="none"
              onClick={onClose}
            >
              Go Back
            </MButton>
          </Box>
        </Box>
      </GenericDialog>
    </Box>
  )
}
